<template>
  <div class="body-alignment-game-container">
    <a class="back-button" @click="goBack">⬅ {{back_label[language]}}</a>

    <div class="game-stats">
      <div class="game-info">{{score_label[language]}}: {{ score }}</div>
      <div class="timer">{{ timeleft_label[language] }}: {{ timeLeft }}s</div>
    </div>

    <div class="ar-view">
      <video id="input_video" autoplay playsinline></video>
      <canvas id="output_canvas"></canvas>
    </div>

    <!-- Food Image at Random Position -->
    <div class="food-target" v-if="currentFood" :style="{ top: `${foodPosition.y}%`, left: `${foodPosition.x}%` }">
      <img :src="currentFood.src" :alt="currentFood.name" class="food-image" />
    </div>


    <MorningWinOverlay :message="overlayMessage[language]" :visible="isMorningWinOverlayVisible" />
    <MorningLoseOverlay :message="overlayMessage[language]" :visible="isMorningLoseOverlayVisible" />
    <NoonWinOverlay :message="overlayMessage[language]" :visible="isNoonWinOverlayVisible" />
    <NoonLoseOverlay :message="overlayMessage[language]" :visible="isNoonLoseOverlayVisible" />
    <AfternoonWinOverlay :message="overlayMessage[language]" :visible="isAfternoonWinOverlayVisible" />
    <AfternoonLoseOverlay :message="overlayMessage[language]" :visible="isAfternoonLoseOverlayVisible" />
    <EveningWinOverlay :message="overlayMessage[language]" :visible="isEveningWinOverlayVisible" />
    <EveningLoseOverlay :message="overlayMessage[language]" :visible="isEveningLoseOverlayVisible" />
    <ProfPepOverlay :message="overlayMessage[language]" :visible="isOverlayVisible" @start-game="onStartGame" />
    <div v-if="cueMessage" class="cue-message">{{ cueMessage[language] }}</div>
    <div v-if="badgeMessage" :class="['badge', badgeType]">{{ badgeMessage }}</div>
  </div>
</template>

<script>
import { Camera } from "@mediapipe/camera_utils";
import { Pose } from "@mediapipe/pose";
import { Howler } from 'howler';
import { sounds } from '@/utils/sounds';
import ProfPepOverlay from '@/components/ProfPepOverlay.vue';
import MorningWinOverlay from '@/components/MorningWinOverlay.vue';
import MorningLoseOverlay from '@/components/MorningLoseOverlay.vue';
import NoonWinOverlay from '@/components/NoonWinOverlay.vue';
import NoonLoseOverlay from '@/components/NoonLoseOverlay.vue';
import AfternoonWinOverlay from '@/components/AfternoonWinOverlay.vue';
import AfternoonLoseOverlay from '@/components/AfternoonLoseOverlay.vue';
import EveningWinOverlay from '@/components/EveningWinOverlay.vue';
import EveningLoseOverlay from '@/components/EveningLoseOverlay.vue';

import pacmanPng from '@/assets/game-elements/pacman.png';
const pacmanImg = new Image();
pacmanImg.src = pacmanPng;

export default {
  components: {
    ProfPepOverlay,
    MorningWinOverlay,
    MorningLoseOverlay,
    NoonWinOverlay,
    NoonLoseOverlay,
    AfternoonWinOverlay,
    AfternoonLoseOverlay,
    EveningWinOverlay,
    EveningLoseOverlay,
  },
  name: "FramesGame",
  data() {
    return {
      audio: null,
      isShuttingDown: false,
      back_label : {en:"Back", el:"Πίσω", it:"Indietro", ro:"Înapoi", uk:"Назад"},
     Round_label: {en:"Round", el:"Γύρος", it:"Round", ro:"Rundă", uk:"Раунд"},
      timeleft_label: {en:"Time left", el:"Χρόνος", it:"Tempo rimanente", ro:"Timp rămas", uk:"Час залишився"},
      score_label: {en:"Score", el:"Πόντοι", it:"Punteggio", ro:"Scor", uk:"Очки"},
      correct_label: {en:"Correct!", el:"Σωστά!", it:"Corretto", ro:"Corect!", uk:"Правильно!"},
      wrong_label: {en:"Wrong...", el:"Λάθος...", it:"Sbagliato", ro:"Greșit...", uk:"Неправильно..."},
      isSyncing: false,

      camera: null,
      scoreThreshold: 100,
      daypart: '', // Initialize daypart
      score: 0,
      timeLeft: 60,
      timer: null,
      frameTimer: null,
      badgeMessage: '',
      badgeType: '',
      cueMessage: 'Align your head within the frame!',
      frame: {},
      frameStartTime: 0,
      gamePhase: 'initialization',
      overlayMessage: {en:'This is the message',el:"Αυτό είναι το μήνυμα"},
      isOverlayVisible: true,
      isMorningWinOverlayVisible: false,
      isMorningLoseOverlayVisible: false,
      isNoonWinOverlayVisible: false,
      isNoonLoseOverlayVisible: false,
      isAfternoonWinOverlayVisible: false,
      isAfternoonLoseOverlayVisible: false,
      isEveningWinOverlayVisible: false,
      isEveningLoseOverlayVisible: false,
      foods: [],
      currentFood: null,
      distance: null,
      foodX: null,
      foodY: null,
      foodPosition: { x: 50, y: 10 },
      foodCollected: false,
      categories: {
        fruits: [
          {
            name: "apple",
            src: require("@/assets/game-elements/fruits/apple.png"),
          },
          {
            name: "apple-2",
            src: require("@/assets/game-elements/fruits/apple-2.png"),
          },
          {
            name: "apricot",
            src: require("@/assets/game-elements/fruits/apricot.png"),
          },
          {
            name: "banana",
            src: require("@/assets/game-elements/fruits/banana.png"),
          },
          {
            name: "cherry",
            src: require("@/assets/game-elements/fruits/cherry.png"),
          },
          {
            name: "grapefruit",
            src: require("@/assets/game-elements/fruits/grapefruit.png"),
          },
          {
            name: "kiwi",
            src: require("@/assets/game-elements/fruits/kiwi.png"),
          },
          {
            name: "grapes",
            src: require("@/assets/game-elements/fruits/grapes.png"),
          },
          {
            name: "nectarine",
            src: require("@/assets/game-elements/fruits/nectarine.png"),
          },
          {
            name: "pear",
            src: require("@/assets/game-elements/fruits/pear.png"),
          },
          {
            name: "pluot",
            src: require("@/assets/game-elements/fruits/pluot.png"),
          },
          {
            name: "strawberry",
            src: require("@/assets/game-elements/fruits/strawberry.png"),
          },
          {
            name: "watermelon",
            src: require("@/assets/game-elements/fruits/watermelon.png"),
          },
        ],
      },

    };
  },

  methods: {
    async goBack() {
      sounds.whoosh.play();
      this.isShuttingDown=true;
      clearInterval(this.timer);
      clearInterval(this.frameTimer);

      if (this.camera) {
        try {
          this.camera.stop();
        } catch (e) {
          console.error("Camera stop failed on unmount:", e);
        }
      }

      // Optionally nullify the camera and pose to prevent further usage
      this.camera = null;

      if (this.pose) {
        this.pose.close(); // dispose pose properly if using pose.setOptions() instance
        this.pose = null;
      }

      // this.saveGameState();
      // this.$router.push({ name: 'GameSelection' });
      // End the game as if the time ran out
      await this.endGame();
    },

    startGame() {
      this.cueMessage = {en:'Get ready!', el:'Ετοιμάσου!', it:'Preparati!', ro:'Pregătește-te!', uk:'Приготуйся!'};
      this.overlayMessage = {en:'Get ready to eat the flying food!', el:'Ετοιμάσου να φας το ιπτάμενο φαγητό!', it:'Preparati a mangiare il cibo volante!', ro:'Pregătește-te să mănânci mâncarea zburătoare!', uk:'Приготуйся їсти літаючу їжу!'};
      this.isOverlayVisible = true;
      this.initializePoseDetection();
    },
    onStartGame() {
      this.foods = Object.values(this.categories).flat();
      this.currentFood = this.foods[Math.floor(Math.random() * this.foods.length)];
      this.randomizeFoodPosition();
      // Resume Howler context for autoplay policies
      Howler.ctx.resume();
      // Hide the overlay and start the game logic
      this.isOverlayVisible = false;
      // Clear the cue message after 5 seconds
      setTimeout(() => {
        this.cueMessage = null;
      }, 5000);
      this.score = 0;
      this.foodCollected = false;

      // Add background music after 3-second delay when overlay is hidden
      setTimeout(() => {
        sounds.bgm_bodymove.play();
      }, 3000);

      this.cueMessage = {en:'Eat the flying food!', el:'Φάε το ιπτάμενο φαγητό!', it:'Mangia il cibo volante!', ro:'Mănâncă mâncarea zburătoare!', uk:'Їж літаючу їжу!'};

      this.timeLeft = 60;
      this.startTimer();
      this.generateFrame();

      // Play a sound
      sounds.get_ready.play();
    },


    randomizeFoodPosition() {
      this.foodPosition.x = Math.random() * 80 + 10; // 10% to 90% of the width
      this.foodPosition.y = Math.random() * 50 + 20; // 10% to 90% of the height
    },




    resetGame() {
      this.score = 0;
      this.timeLeft = 60;
      this.gamePhase = 'initialization';
      this.frame = {};
      clearInterval(this.frameTimer);
    },

    startTimer() {
      clearInterval(this.timer);
      this.timer = setInterval(() => {
        this.timeLeft--;
        if (this.timeLeft <= 0) {
          clearInterval(this.timer);
          clearInterval(this.frameTimer);
          this.endGame();
        }
      }, 1000);
    },

    initializePoseDetection() {
      const videoElement = document.getElementById('input_video');
      const canvasElement = document.getElementById('output_canvas');
      const canvasCtx = canvasElement.getContext('2d');

      videoElement.width = window.innerWidth;
      videoElement.height = window.innerHeight;
      canvasElement.width = window.innerWidth;
      canvasElement.height = window.innerHeight;

      const pose = new Pose({
        locateFile: (file) => `https://cdn.jsdelivr.net/npm/@mediapipe/pose/${file}`,
      });

      pose.setOptions({
        modelComplexity: 0,
        smoothLandmarks: true,
        enableSegmentation: false,
        minDetectionConfidence: 0.5,
        minTrackingConfidence: 0.5,
      });

      pose.onResults((results) => {
        canvasCtx.clearRect(0, 0, canvasElement.width, canvasElement.height);
        canvasCtx.drawImage(
          results.image,
          0,
          0,
          canvasElement.width,
          canvasElement.height
        );

        if (results.poseLandmarks) {
          this.drawLandmarks(results.poseLandmarks, canvasCtx);
          this.processPose(results.poseLandmarks);
        }
      });

      this.camera = new Camera(videoElement, {
        onFrame: async () => {
          if (this.isShuttingDown) return; // Don't send if shutting down
          if (videoElement.readyState >= 2) {
            try {
              await pose.send({ image: videoElement });
            } catch (e) {
              console.error("Error during pose.send:", e);
            }
          }
        },
        width: 320,
        height: 180,
      });

      this.camera.start();
    },

    drawLandmarks(landmarks, ctx) {
      // Draw a single pacman image to represent the mouth by averaging left (9) and right (10) mouth landmarks
      const leftMouth = landmarks[9];
      const rightMouth = landmarks[10];
      if (leftMouth && rightMouth) {
        const x = ((leftMouth.x + rightMouth.x) / 2) * ctx.canvas.width;
        const y = ((leftMouth.y + rightMouth.y) / 2) * ctx.canvas.height;
        if (pacmanImg.complete) {
          const size = window.innerHeight * 0.1; // 10% of viewport height
          ctx.drawImage(pacmanImg, x - size/2, y - size/2, size, size);
        }
      }
    },


    generateFrame() {
      this.frame = {
        x: Math.random() * 0.6 + 0.2, // Random X position (20% to 80% of width)
        y: Math.random() * 0.6 + 0.2, // Random Y position (20% to 80% of height)
        width: 0.3, // 30% of screen width
        height: 0.2, // 20% of screen height
      };
      this.frameStartTime = Date.now();

      clearInterval(this.frameTimer);
      this.frameTimer = setTimeout(() => {
        this.generateFrame(); // Move frame to a new position if not aligned in time
      }, 10000);
    },

    processPose(landmarks) {
      // Get canvas dimensions
      const canvasElement = document.getElementById('output_canvas');
      const canvasWidth = canvasElement.width;
      const canvasHeight = canvasElement.height;

      // Calculate food position in pixels relative to the canvas
      const foodX = (this.foodPosition.x / 100) * canvasWidth;
      const foodY = (this.foodPosition.y / 100) * canvasHeight;

      // Calculate the mouth center position in pixels (average of left and right mouth landmarks)
      const leftMouth = landmarks[9];
      const rightMouth = landmarks[10];
      if (!leftMouth || !rightMouth) return;
      const headX = ((1 - ((leftMouth.x + rightMouth.x) / 2)) * canvasWidth);
      const headY = (((leftMouth.y + rightMouth.y) / 2) * canvasHeight);

      // Distance between mouth and food
      const distance = Math.sqrt((headX - foodX) ** 2 + (headY - foodY) ** 2);
      const fruitSizePx = Math.min(window.innerWidth, window.innerHeight) * 0.25; // 25vmin in pixels
      const hitRadius = fruitSizePx * 0.5; // half the fruit size
      if (distance < hitRadius && !this.foodCollected) {
        this.foodCollected = true;
        const timeTaken = (Date.now() - this.frameStartTime) / 1000;
        if (timeTaken <= 10) {
          const points = Math.max(10 - timeTaken, 0);
          this.score += Math.round(points);
          this.showBadge('success', `+${Math.round(points)} ${this.score_label[this.language]}!`);
          // Play a sound
          sounds.got_one.play();
        }
        this.frameStartTime = Date.now();
        // Hide the current food right away
        this.currentFood = null;
        // After 1 second, show a new food at a random position
        setTimeout(() => {
          this.randomizeFoodPosition();
          this.currentFood = this.foods[Math.floor(Math.random() * this.foods.length)];
          this.foodCollected = false;
        }, 500);
      }
    }
    ,

    showBadge(type, message) {
      this.badgeType = type;
      this.badgeMessage = message;
      setTimeout(() => {
        this.badgeMessage = '';
      }, 2000);
    },

    async updateSeedPoints(seedType, change) {
  const token = localStorage.getItem("token");
  if (!token) return;

  try {
    const response = await fetch("https://erebus2.athenarc.gr/duse/update-seed-points", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ seedType, change }),
    });

    if (response.ok) {
      console.log(`Seed points updated: ${change} to ${seedType}`);
    } else {
      console.error("Failed to update seed points.");
    }
  } catch (error) {
    console.error("Error updating seed points:", error);
  }
},
async endGame() {
  // Guard clause: if overlay is visible, immediately redirect and return
  if (this.isOverlayVisible) {
    this.$router.push({ name: 'GameSelection' });
    return;
  }
  let seedType = "";
  let change = 0;

  if (this.score > this.scoreThreshold) {
    change = 1; // Win: Gain a seed
  } else {
    change = -1; // Lose: Lose a seed
  }
  if (this.score > this.scoreThreshold) {
    // Play a sound
    sounds.win_sound.play();
  } else {
    // Play a sound
    sounds.lose_sound.play();
  }
  if (this.daypart == 'morning') {
    seedType = "blue";

    if (this.score > this.scoreThreshold) {
      this.overlayMessage = {en:`Congratulations! You scored ${this.score} points!`, el:`Συγχαρητήρια! Η βαθμολογία σου είναι ${this.score} πόντοι!`, it:`Congratulazioni! Hai totalizzato ${this.score} punti!`, ro:`Felicitări! Ai obținut ${this.score} puncte!`, uk:`Вітаємо! Ви набрали ${this.score} балів!`};
      this.isMorningWinOverlayVisible = true; // Show overlay at the end
    } else {
      this.overlayMessage = {en:`You scored ${this.score} points. Try again…`, el:`Συγκέντρωσες ${this.score} πόντους. Προσπάθησε ξανά…`, it:`Hai totalizzato ${this.score} punti. Riprova…`, ro:`Ai obținut ${this.score} puncte. Încearcă din nou…`, uk:`Ви набрали ${this.score} балів. Спробуйте ще раз…`};
      this.isMorningLoseOverlayVisible = true; // Show overlay at the end
    }
  } else if (this.daypart == 'noon') {
    seedType = "red";

    if (this.score > this.scoreThreshold) {
      this.overlayMessage = {en:`Congratulations! You scored ${this.score} points!`, el:`Συγχαρητήρια! Η βαθμολογία σου είναι ${this.score} πόντοι!`, it:`Congratulazioni! Hai totalizzato ${this.score} punti!`, ro:`Felicitări! Ai obținut ${this.score} puncte!`, uk:`Вітаємо! Ви набрали ${this.score} балів!`};
      this.isNoonWinOverlayVisible = true; // Show overlay at the end
    } else {
      this.overlayMessage = {en:`You scored ${this.score} points. Try again…`, el:`Συγκέντρωσες ${this.score} πόντους. Προσπάθησε ξανά…`, it:`Hai totalizzato ${this.score} punti. Riprova…`, ro:`Ai obținut ${this.score} puncte. Încearcă din nou…`, uk:`Ви набрали ${this.score} балів. Спробуйте ще раз…`};
      this.isNoonLoseOverlayVisible = true; // Show overlay at the end
    }
  } else if (this.daypart == 'afternoon') {
    seedType = "green";
    if (this.score > this.scoreThreshold) {
      this.overlayMessage = {en:`Congratulations! You scored ${this.score} points!`, el:`Συγχαρητήρια! Η βαθμολογία σου είναι ${this.score} πόντοι!`, it:`Congratulazioni! Hai totalizzato ${this.score} punti!`, ro:`Felicitări! Ai obținut ${this.score} puncte!`, uk:`Вітаємо! Ви набрали ${this.score} балів!`};
      this.isAfternoonWinOverlayVisible = true; // Show overlay at the end
    } else {
      this.overlayMessage = {en:`You scored ${this.score} points. Try again…`, el:`Συγκέντρωσες ${this.score} πόντους. Προσπάθησε ξανά…`, it:`Hai totalizzato ${this.score} punti. Riprova…`, ro:`Ai obținut ${this.score} puncte. Încearcă din nou…`, uk:`Ви набрали ${this.score} балів. Спробуйте ще раз…`};
      this.isAfternoonLoseOverlayVisible = true; // Show overlay at the end
    }
  } else if (this.daypart == 'night') {
    seedType = "yellow";
    if (this.score > this.scoreThreshold) {
      this.overlayMessage = {en:`Congratulations! You scored ${this.score} points!`, el:`Συγχαρητήρια! Η βαθμολογία σου είναι ${this.score} πόντοι!`, it:`Congratulazioni! Hai totalizzato ${this.score} punti!`, ro:`Felicitări! Ai obținut ${this.score} puncte!`, uk:`Вітаємо! Ви набрали ${this.score} балів!`};
      this.isEveningWinOverlayVisible = true; // Show overlay at the end
    } else {
      this.overlayMessage = {en:`You scored ${this.score} points. Try again…`, el:`Συγκέντρωσες ${this.score} πόντους. Προσπάθησε ξανά…`, it:`Hai totalizzato ${this.score} punti. Riprova…`, ro:`Ai obținut ${this.score} puncte. Încearcă din nou…`, uk:`Ви набрали ${this.score} балів. Спробуйте ще раз…`};
      this.isEveningLoseOverlayVisible = true; // Show overlay at the end
    }
  }
  // Send seed update to the backend
  await this.updateSeedPoints(seedType, change);

  setTimeout(() => {
    this.$router.push({ name: 'GameSelection' }); // Redirect after delay
  }, 3000); // 3 seconds delay
},

    async storeInIndexedDB(gameState) {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open("GameDB", 1);
    request.onupgradeneeded = function (event) {
      const db = event.target.result;
      if (!db.objectStoreNames.contains("gameStates")) {
        db.createObjectStore("gameStates", { keyPath: "id", autoIncrement: true });
      }
    };

    request.onsuccess = function (event) {
      const db = event.target.result;
      const transaction = db.transaction("gameStates", "readwrite");
      const store = transaction.objectStore("gameStates");

      // Check for existing game state before saving
      const getAllRequest = store.getAll();
      getAllRequest.onsuccess = function () {
        const existingRecords = getAllRequest.result;
        const duplicate = existingRecords.find(
          (record) => record.gameName === gameState.gameName && record.datePlayed === gameState.datePlayed
        );

        if (!duplicate) {
          store.add(gameState);
        } else {
          console.warn("Duplicate game state detected. Skipping save.");
        }

        resolve();
      };

      getAllRequest.onerror = function (event) {
        reject("Error checking existing records:", event.target.error);
      };
    };

    request.onerror = function (event) {
      reject("Error opening IndexedDB:", event.target.error);
    };
  });
}
,

async getUnsyncedGameStates() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open("GameDB", 1);

    request.onsuccess = function (event) {
      const db = event.target.result;
      const transaction = db.transaction("gameStates", "readonly");
      const store = transaction.objectStore("gameStates");

      const getAllRequest = store.getAll();
      getAllRequest.onsuccess = function () {
        resolve(getAllRequest.result);
      };
      getAllRequest.onerror = function (event) {
        reject("Error retrieving game states:", event.target.error);
      };
    };
  });
},

async deleteSyncedGameStates(syncedGames) {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open("GameDB", 1);

    request.onsuccess = function (event) {
      const db = event.target.result;
      const transaction = db.transaction("gameStates", "readwrite");
      const store = transaction.objectStore("gameStates");

      syncedGames.forEach((game) => {
        store.delete(game.id); // Delete only specific records
      });

      resolve();
    };

    request.onerror = function (event) {
      reject("Error clearing IndexedDB:", event.target.error);
    };
  });
}
,
async syncWithMongo() {
  if (this.isSyncing) return; // Prevent duplicate sync
  this.isSyncing = true;

  const token = localStorage.getItem("token");
  if (!token) {
    console.error("User not authenticated. Cannot sync.");
    this.isSyncing = false;
    return;
  }

  try {
    const unsyncedGames = await this.getUnsyncedGameStates();
    if (unsyncedGames.length === 0) {
      console.log("No game states to sync.");
      this.isSyncing = false;
      return;
    }

    const response = await fetch("https://erebus2.athenarc.gr/duse/save-game-state", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ gameStates: unsyncedGames }),
    });

    if (response.ok) {
      console.log("Game states synced successfully.");
      await this.deleteSyncedGameStates(unsyncedGames);
    } else {
      console.error("Failed to sync game states.");
    }
  } catch (error) {
    console.error("Error syncing with MongoDB:", error);
  } finally {
    this.isSyncing = false; // Ensure flag is reset
  }
},
    async saveGameState() {
  const token = localStorage.getItem("token");
  const username = localStorage.getItem("username");

  if (!username) {
    console.error("User not logged in. Cannot save game state.");
    return;
  }

  const gameState = {
    username,
    gameName: this.$route.name,
    score: this.score,
    datePlayed: new Date().toISOString(),
  };

  try {
    // Store in IndexedDB regardless of network status
    await this.storeInIndexedDB(gameState);

    if (navigator.onLine && token) {
      await this.syncWithMongo();
    } else {
      console.warn("No internet connection. Game state saved locally.");
    }
  } catch (error) {
    console.error("Error saving game state:", error);
  }
},

  },
  created()
  {
    this.language = localStorage.getItem("language") || "en"; // Default to English
    console.log("LANGUAGE "+this.language);
  },

  mounted() {
  console.log("Current route path:", this.$route.path);

  const daypart = this.$route.path.split("/")[2] || "daypart";
  this.daypart = daypart;
  this.startGame();

  // Check for internet connectivity
  window.addEventListener("online", async () => {
    console.log("Back online! Syncing game states...");
    await this.syncWithMongo();
  });
},
  beforeUnmount() {
    clearInterval(this.timer);
    if (this.camera) {
      try {
        this.camera.stop();
      } catch (e) {
        console.error("Camera stop failed on unmount:", e);
      }
    }

    // Optionally nullify the camera and pose to prevent further usage
    this.camera = null;

    if (this.pose) {
      this.pose.close(); // dispose pose properly if using pose.setOptions() instance
      this.pose = null;
    }
    if (sounds.bgm_bodymove) {
      sounds.bgm_bodymove.stop();
    }
    this.saveGameState();
  },
  beforeRouteLeave(to, from, next) {
    // Save game state when the user navigates away
    this.saveGameState();
    next();
  },
};
</script>

<style scoped>
.body-alignment-game-container {
  position: relative;
  width: 100vw;
  height: 100vh;
  background-size: cover;
}

.ar-view {
  position: relative;
  width: 100%;
  height: 100%;
}

video,
canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: auto;
  /* Maintain aspect ratio */
  height: 100%;
  /* Full height of the screen */
  object-fit: cover;
  /* Avoid distortion */
  transform: scaleX(-1);
  /* Mirror for selfie mode */
}

.game-stats {
  position: absolute;
  top: 70px;
  /* Fixed position to prevent being pushed */
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  /* Higher than video and canvas */
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 90%;
  margin: 0 auto;
  padding: 10px;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 8px;
  color: white;
  font-size: 20px;
}

.cue-message {
  position: absolute;
  top: 170px;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 24px;
  color: white;
  background-color: rgba(0, 0, 0, 0.7);
  padding: 10px 20px;
  border-radius: 10px;
  text-align: center;
}

.badge {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 24px;
  color: white;
  padding: 10px 20px;
  border-radius: 10px;
  text-align: center;
}

.badge.success {
  background-color: green;
}

.back-button {
  z-index: 1001;
}



.food-target {
  position: absolute;
  width: 25vmin;
  height: 25vmin;
  transform: translate(-50%, -50%);
}

.food-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

</style>